import { Checkbox } from "@mui/material";
import { Dropdown, Pagination } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import Table from "../table/Table";
import "./selectableTable.scss";

const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];

export default function SelectableTable({
  rows,
  headerData,
  selectedIds,
  onSelectionChange,
  className,
  noDataLabel,
  rowsPerPage,
  rowsPerPageOptions,
  showRowsPerPage,
  highlightSelection,
  rowIdKey,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(rowsPerPage);

  const rowIds = useMemo(
    () =>
      rows
        .map((row) => row[rowIdKey])
        .filter((rowId) => rowId !== null && rowId !== undefined),
    [rowIdKey, rows]
  );
  const rowIdsKey = JSON.stringify(rowIds);
  const allSelected =
    rowIds.length > 0 && rowIds.every((rowId) => selectedIds.includes(rowId));
  const someSelected =
    !allSelected && rowIds.some((rowId) => selectedIds.includes(rowId));
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const firstRowIndex = (currentPage - 1) * pageSize;
  const visibleRows = rows.slice(firstRowIndex, firstRowIndex + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, rowIdsKey]);

  useEffect(() => {
    setPageSize(rowsPerPage);
  }, [rowsPerPage]);

  const toggleAll = useCallback(() => {
    onSelectionChange(
      allSelected
        ? selectedIds.filter((selectedId) => !rowIds.includes(selectedId))
        : [...new Set([...selectedIds, ...rowIds])]
    );
  }, [allSelected, onSelectionChange, rowIds, selectedIds]);

  const toggleRow = useCallback(
    (rowId) => {
      onSelectionChange(
        selectedIds.includes(rowId)
          ? selectedIds.filter((selectedId) => selectedId !== rowId)
          : [...selectedIds, rowId]
      );
    },
    [onSelectionChange, selectedIds]
  );

  const selectableHeaders = useMemo(
    () => [
      {
        columnKey: "_selection",
        header: (
          <Checkbox
            inputProps={{ "aria-label": "Select all rows" }}
            checked={allSelected}
            indeterminate={someSelected}
            onChange={toggleAll}
            size="small"
            sx={{ padding: 0 }}
          />
        ),
        className: "checkbox-col",
        itemClassName: "checkbox-col",
      },
      ...headerData,
    ],
    [allSelected, headerData, someSelected, toggleAll]
  );

  const selectableRows = useMemo(
    () =>
      visibleRows.map((row, index) => {
        const rowId = row[rowIdKey];
        const isSelected = selectedIds.includes(rowId);
        return {
          ...row,
          _selection: (
            <Checkbox
              inputProps={{
                "aria-label": `Select row ${firstRowIndex + index + 1}`,
              }}
              checked={isSelected}
              onChange={() => toggleRow(rowId)}
              size="small"
              sx={{ padding: 0 }}
            />
          ),
          _rowClassName:
            highlightSelection && isSelected
              ? "selectable-table__row--selected"
              : row._rowClassName,
        };
      }),
    [
      firstRowIndex,
      highlightSelection,
      rowIdKey,
      selectedIds,
      toggleRow,
      visibleRows,
    ]
  );

  const pageSizeItems = useMemo(
    () =>
      rowsPerPageOptions.map((size) => ({
        id: size,
        displayText: String(size),
      })),
    [rowsPerPageOptions]
  );

  return (
    <div className="selectable-table">
      <Table
        headerData={selectableHeaders}
        rowData={selectableRows}
        className={className}
        noDataLabel={noDataLabel}
      />
      {(showRowsPerPage || pageCount > 1) && (
        <div className="selectable-table__footer">
          {showRowsPerPage && (
            <div className="selectable-table__page-size">
              <span>Rows per page</span>
              <Dropdown
                childList={pageSizeItems}
                selection="single"
                size="small"
                selectedValue={[{ id: pageSize, displayText: String(pageSize) }]}
                setSelectedValue={(value) => {
                  const nextSize = Number(value?.[0]?.id);
                  if (nextSize) setPageSize(nextSize);
                }}
              />
            </div>
          )}
          {pageCount > 1 && (
            <Pagination
              currentPage={currentPage}
              onUpdate={setCurrentPage}
              pageCount={pageCount}
              size="medium"
              variant="number"
            />
          )}
        </div>
      )}
    </div>
  );
}

SelectableTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  headerData: PropTypes.arrayOf(
    PropTypes.shape({
      columnKey: PropTypes.string.isRequired,
      header: PropTypes.node.isRequired,
    })
  ).isRequired,
  selectedIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  noDataLabel: PropTypes.string,
  rowsPerPage: PropTypes.number,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  showRowsPerPage: PropTypes.bool,
  highlightSelection: PropTypes.bool,
  rowIdKey: PropTypes.string,
};

SelectableTable.defaultProps = {
  className: "",
  noDataLabel: "No data available",
  rowsPerPage: 10,
  rowsPerPageOptions: DEFAULT_PAGE_SIZES,
  showRowsPerPage: false,
  highlightSelection: false,
  rowIdKey: "id",
};
